import React, { useEffect, useRef } from 'react';
import { Shield, Crown, Leaf, Award, Star, Droplets, ArrowRight, Wind } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import imgHero from '../assets/1d511ceb-a632-4d1d-8887-2b8c7a6994c3.jpg';
import imgHeritage from '../assets/2bc9bf8d-eb81-4664-b1ff-0f286998c3f2.jpg';
import imgHeritage2 from '../assets/72fe59ce-c3b1-422a-85ae-788512069a81 (1).jpg';
import imgWorkshop from '../assets/e119f159-e1f5-4ec6-9008-380a66731113.jpg';
import imgVision from '../assets/f277d437-684a-4714-9e79-1c60e1746e52.jpg';
import imgOud from '../assets/04d1b47f7a9aa37e71ab857d6e511ec8.jpg';
import imgRose from '../assets/f5565a7eae2b771734cf6407f5eb309c.jpg';
import imgAmber from '../assets/f92f3ad9e5a0452775b81a945b7effbc.jpg';
import './AboutPage.css';

gsap.registerPlugin(ScrollTrigger);

// ── Liquid Background Component (Shader-like Effect) ──
// eslint-disable-next-line no-unused-vars
const LiquidBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById('liquid-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height, particles = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.radius = Math.random() * 300 + 100;
        this.color = Math.random() > 0.5 ? 'rgba(212, 175, 55, 0.05)' : 'rgba(0, 0, 0, 0.04)';
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < -this.radius) this.x = width + this.radius;
        if (this.x > width + this.radius) this.x = -this.radius;
        if (this.y < -this.radius) this.y = height + this.radius;
        if (this.y > height + this.radius) this.y = -this.radius;
      }
      draw() {
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
        gradient.addColorStop(0, this.color);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const init = () => {
      resize();
      particles = Array.from({ length: 8 }, () => new Particle());
      animate();
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      requestAnimationFrame(animate);
    };

    init();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return <canvas id="liquid-canvas" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />;
};

const AboutPage = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const ctx = gsap.context(() => {
      // 1. Cinematic Entrance
      const tl = gsap.timeline();
      tl.from('.hero-split-image', {
        x: 100,
        opacity: 0,
        duration: 2,
        ease: 'expo.out'
      })
      .from('.hero-main-title span', {
        y: 100,
        rotateX: -90,
        opacity: 0,
        duration: 1.5,
        stagger: 0.1,
        ease: 'expo.out'
      }, "-=1.5")
      .from('.hero-sub-text', {
        y: 30,
        opacity: 0,
        duration: 1,
        ease: 'power3.out'
      }, "-=1");

      // Disabled all ScrollTriggers to fix heavy scroll lag and getting stuck
      /*
      // 2. Parallax Effect for "Philosophy" background text
      gsap.to('.bg-text-parallax', {
        x: -200,
        scrollTrigger: {
          trigger: '.philosophy-section',
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.5
        }
      });

      // 4. Reveal Animations
      gsap.utils.toArray('.reveal-up').forEach(el => {
        gsap.from(el, {
          y: 60,
          opacity: 0,
          duration: 1.2,
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        });
      });

      // 5. Image Scale on Scroll
      gsap.utils.toArray('.scale-img-wrap img').forEach(img => {
        gsap.to(img, {
          scale: 1.2,
          scrollTrigger: {
            trigger: img,
            start: "top bottom",
            end: "bottom top",
            scrub: true
          }
        });
      });
      */
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="about-page-v5" ref={containerRef}>
      {/* ── SECTION 01: CINEMATIC HERO (SPLIT) ── */}
      <section className="hero-v5">
        <div className="hero-container">
          <div className="hero-text-content">
            <span className="hero-tagline">ESTABLISHED 2023 — PAKISTAN</span>
            <h1 className="hero-main-title">
              <span>Beyond</span><br />
              <span className="italic-gold">Fragrance</span><br />
              <span>Existence</span>
            </h1>
            <p className="hero-sub-text">
              We capture the invisible poetry of the soul. Escentrum is a sanctuary of olfactory art, where rare essences meet avant-garde craftsmanship.
            </p>
            <div className="hero-scroll-indicator">
              <div className="line"></div>
              <span>EXPLORE THE LEGACY</span>
            </div>
          </div>
          <div className="hero-visual-split">
            <div className="hero-split-image">
              <img src={imgHero} alt="Escentrum Masterpiece" />
              <div className="image-overlay-glow"></div>
            </div>
            <div className="hero-floating-badge">
              <Star color="#d4af37" fill="#d4af37" size={24} />
              <span>PREMIUM COLLECTION</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 02: THE PHILOSOPHY (MINIMALIST) ── */}
      <section className="philosophy-section">
        <div className="bg-text-parallax">PHILOSOPHY</div>
        <div className="container">
          <div className="philosophy-grid">
            <div className="phil-left reveal-up">
              <h2 className="editorial-title">
                The Science of<br />
                <span className="gold-text">Memory</span>
              </h2>
            </div>
            <div className="phil-right reveal-up">
              <p className="p-lead">
                A scent is not just a smell; it is a time machine. It bridges the gap between the present and the most profound moments of your life.
              </p>
              <div className="phil-features">
                <div className="phil-item">
                  <Droplets color="#d4af37" />
                  <h4>Pure Oils</h4>
                  <p>100% natural absolutes sourced from the most prestigious botanical gardens.</p>
                </div>
                <div className="phil-item">
                  <Wind color="#d4af37" />
                  <h4>Sillage</h4>
                  <p>Engineered for an eternal trail that lingers long after you leave the room.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 03: THE MASTERPIECEBLUEPRINT (VISUAL CALLOUTS) ── */}
      <section className="blueprint-section reveal-up">
        <div className="container">
          <div className="blueprint-header">
            <span className="section-tag">THE ANATOMY</span>
            <h2 className="section-title">Anatomy of a <span>Masterpiece</span></h2>
          </div>
          <div className="blueprint-visual">
            <div className="blueprint-main-img scale-img-wrap">
              <img src={imgHeritage2} alt="Masterpiece Bottle" />
            </div>
            <div className="callout c1">
              <div className="dot"></div>
              <div className="info">
                <h5>Crystal Glass</h5>
                <p>Hand-polished in Italy for unparalleled clarity.</p>
              </div>
            </div>
            <div className="callout c2">
              <div className="dot"></div>
              <div className="info">
                <h5>The Essence</h5>
                <p>97% concentration of pure, rare oils.</p>
              </div>
            </div>
            <div className="callout c3">
              <div className="dot"></div>
              <div className="info">
                <h5>Gold Seal</h5>
                <p>24k gold leaf detailing on the signature cap.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 04: ATELIER PIN (CINEMATIC MOSAIC) ── */}
      <section className="atelier-pin-section">
        <div className="atelier-bg-glow"></div>
        <div className="container">
          <div className="atelier-header reveal-up">
            <span className="section-tag">THE ATELIER</span>
            <h2 className="section-title">Where <span>Alchemy</span> Happens</h2>
          </div>
          <div className="atelier-mosaic-v5">
            <div className="mosaic-v5-item large reveal-up">
              <img src={imgWorkshop} alt="The Lab" />
              <div className="item-caption">
                <h4>Phase 01: Extraction</h4>
                <p>Gentle steam distillation preserves the soul of the botanical.</p>
              </div>
            </div>
            <div className="mosaic-v5-item small reveal-up">
              <img src={imgHeritage} alt="Ingredients" />
              <div className="item-caption">
                <h4>Phase 02: Maturation</h4>
                <p>Oils age in darkness for 180 days to reach peak complexity.</p>
              </div>
            </div>
            <div className="mosaic-v5-item small reveal-up">
              <img src={imgVision} alt="Final Polish" />
              <div className="item-caption">
                <h4>Phase 03: Bottling</h4>
                <p>Every bottle is hand-polished and inspected by a master artisan.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 05: GLOBAL SOURCING (MAP SPOTLIGHT) ── */}
      <section className="sourcing-v5 reveal-up">
        <div className="container">
          <div className="sourcing-content">
            <div className="sourcing-text">
              <span className="section-tag">GLOBAL EXPEDITION</span>
              <h2 className="section-title">Rarest <span>Ingredients</span></h2>
              <p className="p-desc">We traverse the globe from the valleys of Grasse to the deep forests of Southeast Asia to find ingredients that no one else can.</p>
              <div className="sourcing-list">
                <div className="s-item">
                  <span className="s-name">ROYAL OUD</span>
                  <span className="s-loc">Cambodia</span>
                </div>
                <div className="s-item">
                  <span className="s-name">DAMASK ROSE</span>
                  <span className="s-loc">Bulgaria</span>
                </div>
                <div className="s-item">
                  <span className="s-name">BLACK AMBER</span>
                  <span className="s-loc">Madagascar</span>
                </div>
              </div>
            </div>
            <div className="sourcing-images">
              <div className="img-stack s1"><img src={imgOud} alt="Oud" /></div>
              <div className="img-stack s2"><img src={imgRose} alt="Rose" /></div>
              <div className="img-stack s3"><img src={imgAmber} alt="Amber" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 06: PILLARS (INTERACTIVE CARDS) ── */}
      <section className="pillars-v5">
        <div className="container">
          <div className="section-header text-center reveal-up">
            <span className="section-tag">OUR VALUES</span>
            <h2 className="section-title">The <span>Pillars</span> of Escentrum</h2>
          </div>
          <div className="pillars-grid-v5">
            {[
              { icon: <Shield />, title: "Authenticity", desc: "No fillers, no shortcuts. Only the purest botanical extracts and high-purity aromachemicals." },
              { icon: <Crown />, title: "Exclusivity", desc: "Small-batch productions designed for the few who appreciate true olfactory rarity." },
              { icon: <Leaf />, title: "Sustainability", desc: "Sustainably harvested raw materials and eco-conscious glass manufacturing." },
              { icon: <Award />, title: "Mastery", desc: "Every formula is a collaboration with award-winning master perfumers." },
            ].map((pillar, i) => (
              <div key={i} className="pillar-card-v5 reveal-up">
                <div className="pillar-card-inner">
                  <div className="pillar-card-front" data-desc={pillar.desc}>
                    <div className="pillar-icon-wrap">{pillar.icon}</div>
                    <h3>{pillar.title}</h3>
                  </div>
                  <div className="pillar-card-back">
                    <div className="back-icon-mini">{pillar.icon}</div>
                    <p>{pillar.desc}</p>
                    <div className="pillar-line"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 07: CLOSING CALL TO ACTION ── */}
      <section className="about-footer-cta reveal-up">
        <div className="cta-parallax-bg">
          <img src={imgHero} alt="" />
        </div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Begin Your <span>Scent Journey</span></h2>
            <p className="cta-desc">Discover the collection that defines modern luxury.</p>
            <a href="/shop" className="masterpiece-btn">
              <span>EXPLORE COLLECTIONS</span>
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
